import { useRoute } from 'vue-router';
import { useUiStore } from '../ui/store';
import { computed, ComputedRef, Ref } from 'vue';
import { IRouteMeta } from '@/core/models/routing';
import { AppCoreEnums } from '../../core/shared/enums';
import { AppPermissions } from '../../shared/permissions';
import useRouterUtils from '@/core/router-utils/route-utils';
import { ROUTE_PATH_NEW } from '../../shared/constants';
import { CollapsibleTableContainerData, CollapsibleTableInnerContainerData, ICoreState, TableBasedContainerData } from './models';

const useStoreGetters = (state: Ref<ICoreState>) => {
  const route = useRoute();
  const uiStore = useUiStore();
  const { hasContainerChildRoutes } = useRouterUtils();

  const getTableBasedContainerData = (containerType: AppCoreEnums.ContainerType): ComputedRef<TableBasedContainerData> =>
    computed(() => {
      switch (containerType) {
        case AppCoreEnums.ContainerType.CollapsibleTableContainer:
          return state.value.collapsibleTableContainerData;

        case AppCoreEnums.ContainerType.CollapsibleTableInnerContainer:
          return state.value.collapsibleTableInnerContainerData;

        case AppCoreEnums.ContainerType.TableContainer:
          return state.value.tableContainerData;

        case AppCoreEnums.ContainerType.TableInnerContainer:
          return state.value.tableInnerContainerData;

        case AppCoreEnums.ContainerType.DrawerContainer:
          return state.value.drawerContainerData;

        case AppCoreEnums.ContainerType.ListContainer:
          return state.value.listContainerData;

        default:
          return new TableBasedContainerData();
      }
    });

  const isCollapsibleTableContainerMinimized = computed(() =>
    hasContainerChildRoutes(route, AppCoreEnums.ContainerType.CollapsibleContainer, true)
  );

  const getUserPreferenceByKey = (key: string | undefined) =>
    computed(() => uiStore.userPreferences?.find((x) => x.preferenceKey === key)?.preferenceValue);

  const hasPermission = (permissionToCheck: string) => {
    return computed(() => {
      if (!permissionToCheck) {
        return true;
      }

      const userPermissions = uiStore.sessionUserInfo?.permissions;
      if (!userPermissions) {
        return false;
      }

      if (userPermissions.indexOf(AppPermissions.AccessAll) >= 0) {
        return true;
      }

      return userPermissions.indexOf(permissionToCheck) >= 0;
    });
  };

  const isActiveRouteNew = computed((): boolean => {
    if (!route.matched?.length) return false;

    return route.matched[route.matched.length - 1]?.path.endsWith('/' + ROUTE_PATH_NEW);
  });

  const getTableBasedContainerSelectedRowTitle = (
    routeParamIdName: 'id' | 'id2' | 'id3',
    containerType:
      | AppCoreEnums.ContainerType.CollapsibleTableContainer
      | AppCoreEnums.ContainerType.CollapsibleTableInnerContainer
  ) => {
    const containerData = getTableBasedContainerData(containerType) as ComputedRef<
      CollapsibleTableContainerData | CollapsibleTableInnerContainerData
    >;

    const selectedRow = containerData.value.options.rows.find(
      (x) => x[containerData.value.options.idPropertyName] == containerData.value.routeParams[routeParamIdName]
    );
    if (selectedRow) {
      const routeMatchedMeta = route.matched.find((x) => (x.meta as IRouteMeta)?.containerType == containerType)
        ?.meta as IRouteMeta;
      return selectedRow[routeMatchedMeta.collapsibleTableTitleField || ''];
    }

    return undefined; //`Container ${AppCoreEnums.ContainerType[containerType]}  titleField not set in route def!`;
  };

  return {
    hasPermission,
    isActiveRouteNew,
    getUserPreferenceByKey,
    getTableBasedContainerData,
    isCollapsibleTableContainerMinimized,
    getTableBasedContainerSelectedRowTitle,
  };
};

export default useStoreGetters;
